import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { SpecificationTaskCreateRepository } from '../../services/specification-task-create-repository';
import type { SpecificationLangDetectResult, SpecificationTaskCreatePayload } from '../../types/specification-task-create';
import { DetectLanguageApi } from '../../apis/detect-language';
import { GetInputContentApi } from '../../apis/get-input-content';
import { InitAiSpecificationTaskApi } from '../../apis/init-ai-specification-task';
import { I18nService } from '@telecom/shared/services/i18n';
import type { SpecificationJurisdiction } from '../../enums';

@injectable()
export class SpecificationTaskCreateRepositoryImpl implements SpecificationTaskCreateRepository {
    constructor(
        @inject(DetectLanguageApi)
        private _detectLanguageApi: DetectLanguageApi,
        @inject(GetInputContentApi)
        private _getInputContentApi: GetInputContentApi,
        @inject(InitAiSpecificationTaskApi)
        private _initAiSpecificationTaskApi: InitAiSpecificationTaskApi,
        @inject(I18nService)
        private _i18nService: I18nService,
    ) {}

    async getHistoryInput(taskId: string): Promise<Partial<SpecificationTaskCreatePayload>> {
        const response = await this._getInputContentApi.getInputContent({
            task_id: taskId,
        });

        return {
            jurisdiction: response.jurisdiction as SpecificationJurisdiction,
            inventionDisclosure: response.disclosure,
            claims: response.claim,
        };
    }

    async langDetect(payload: SpecificationTaskCreatePayload): Promise<SpecificationLangDetectResult> {
        const response = await this._detectLanguageApi.detectLanguage({
            claim: payload.claims,
            disclosure: payload.inventionDisclosure,
            jurisdiction: payload.jurisdiction,
        });

        if (response.result === 'success') {
            return {
                success: true,
                recommendJurisdiction: payload.jurisdiction,
                messageTitle: this._i18nService.t('aiSpecification.recommendJurisdiction'),
                message: this._i18nService.t('aiSpecification.recommendJurisdictionTips'),
            };
        }

        if (response.operation === 'suggest') {
            return {
                success: false,
                recommendJurisdiction: response.suggestion as SpecificationJurisdiction,
                messageTitle: this._i18nService.t('aiSpecification.recommendJurisdiction'),
                message: this._i18nService.t('aiSpecification.recommendJurisdictionTips'),
            };
        }

        if (response.operation === 'modify') {
            return {
                success: false,
                recommendJurisdiction: null,
                messageTitle: this._i18nService.t('aiSpecification.languageInconsistencyTips'),
                message: this._i18nService.t('aiSpecification.backToModify'),
            };
        }

        return {
            success: false,
            messageTitle: this._i18nService.t('aiSpecification.languageNotSupportedTips'),
            message: this._i18nService.t('aiSpecification.backToModify'),
            recommendJurisdiction: null,
        };
    }

    async createTask(taskId: string, payload: SpecificationTaskCreatePayload): Promise<void> {
        await this._initAiSpecificationTaskApi.initAiSpecificationTask({
            task_id: taskId,
            claim: payload.claims,
            disclosure: payload.inventionDisclosure,
            jurisdiction: payload.jurisdiction,
        });
    }
}
